import { differenceInDays } from "date-fns";
import { inject } from "mobx-react";
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { DownloadIcon } from "../../../../../core/components/icons/DownloadIcon.component";
import type { Store } from "@inferno/renderer-shared-core";
import { AgentOSType } from "@inferno/renderer-shared-core";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { isPWSSessionBackend, PWSSessionState } from "@inferno/renderer-shared-core";
import { Button, ButtonKind, FauxButton, Heading } from "../../../../../core/ui";
import { COAST_SHOW_TYPE } from "../../../lib/coastContent";
import { CoastShowAudioBoxMP3DownloadNotice } from "./CoastShowAudioBoxMP3DownloadNotice.component";
import { CoastShowAudioBoxPlayerLink } from "./CoastShowAudioBoxPlayerLink.component";
import { getShowZypeSlugs } from "../../../lib";
import { CoastShowPayloadFields } from "../CoastContent.types";
import { coastMediaService } from "../../../../../core/services/Pws";

interface CoastShowAudioboxMP3Props {
  media: Record<string, CoastMediaItem | null>;
  osType: AgentOSType | null;
  store?: Store;
  showDate: Date | number;
}

interface CoastShowAudioboxMP3LoaderProps {
  osType: AgentOSType | null;
  showType: COAST_SHOW_TYPE;
  showDate: Date | number;
  store?: Store;
  show: CoastShowPayloadFields;
}

type CoastMediaItem = {
  mp3Url: string;
  episodeSlug: string;
};

const mediaLoader = async (
  showDate: number | Date,
  session: PWSSessionState,
  show: CoastShowPayloadFields,
): Promise<Record<string, CoastMediaItem | null>> => {
  const results = { "1": null, "2": null, "3": null, "4": null };
  if (session.accessToken && session.hasActiveSubscription) {
    const mp3Links = await coastMediaService.getMP3LinksBulk(showDate, session.accessToken);
    const episodes = await coastMediaService.getMediaEpisodesBySlugs(getShowZypeSlugs(show), session.accessToken);

    (Object.keys(mp3Links) as Array<string>).forEach((key, index) => {
      results[key] = {
        mp3Url: mp3Links[key],
        episodeSlug: episodes?.videos[index]?.slug ?? null,
      } as CoastMediaItem;
    });

    return Promise.resolve(results);
  }

  return Promise.resolve(results);
};

const CoastShowAudioboxMP3 = inject("store")(({ store, osType, media, showDate }: CoastShowAudioboxMP3Props) => {
  const session = store?.session.currentSession;
  const handleClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();

      if (!isPWSSessionBackend(session) || session.state.hasActiveSubscription) {
        return null;
      }

      if (session.state.authenticated) {
        session.showMissingSubscriptionPopup();
      } else {
        session.login();
      }
    },
    [session],
  );

  if (!store) {
    return null;
  }

  return (
    <>
      <section className="audiobox-section coast-mp3">
        <Heading level={3}>Show Audio</Heading>
        <div className="audiobox-section-value">
          {Object.entries(media).map(([hour, value]) => {
            const { mp3Url, episodeSlug } = value ?? { mp3Url: "", episodeSlug: "" };
            return mp3Url ? (
              <div className="download-hour" key={`hour-${hour}`}>
                <FauxButton
                  kind={ButtonKind.CUSTOMIZED}
                  className="media-link"
                  download="download"
                  key={hour}
                  to={mp3Url}
                >
                  Hour {hour}
                </FauxButton>
                <div className="icon-block">
                  <CoastShowAudioBoxPlayerLink
                    showDate={showDate}
                    showVolumeIcon={false}
                    frontMatter={false}
                    episodeSlug={episodeSlug}
                  />
                  <FauxButton kind={ButtonKind.CUSTOMIZED} className="download-link" download="download" to={mp3Url}>
                    <DownloadIcon />
                  </FauxButton>
                </div>
              </div>
            ) : (
              <div className="download-hour" key={`hour-${hour}`}>
                <Button className="media-link" click={handleClick} kind={ButtonKind.CUSTOMIZED}>
                  Hour {hour}
                </Button>
                <div className="icon-block">
                  <CoastShowAudioBoxPlayerLink
                    showDate={showDate}
                    showVolumeIcon={false}
                    frontMatter={false}
                    episodeSlug={episodeSlug}
                  />
                  <Button className="media-link" click={handleClick} kind={ButtonKind.CUSTOMIZED}>
                    <DownloadIcon />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <CoastShowAudioBoxMP3DownloadNotice osType={osType} />
    </>
  );
});

export const CoastShowAudioboxMP3Loader = inject("store")(
  ({ store, showDate, showType, show, ...rest }: CoastShowAudioboxMP3LoaderProps) => {
    const session = store?.session.currentSession;
    if (!store || !isPWSSessionBackend(session)) {
      return null;
    }

    const [hasActiveSubscription, setHasActiveSubscription] = useState(session.state.hasActiveSubscription);

    const handleSessionUpdate = useCallback(
      (state: PWSSessionState) => setHasActiveSubscription(state.hasActiveSubscription),
      [],
    );

    useEffect(() => {
      session.onStatusChanged.subscribe(handleSessionUpdate);
      return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
    }, [handleSessionUpdate, session.onStatusChanged]);

    // hide mp3 downloads on regular shows that are more than 7 years old when comparing the difference in days
    if (showType === COAST_SHOW_TYPE.REGULAR && differenceInDays(new Date(), showDate) > 7 * 365) {
      return null;
    }

    return (
      <Remote
        loader={() => mediaLoader(showDate, session.state, show)}
        cacheKey={`coast-show-audiobox-mp3-${Number(showDate)}-${hasActiveSubscription}`}
        showLoading={true}
      >
        {({ data }) => (data ? <CoastShowAudioboxMP3 showDate={showDate} media={data} {...rest} /> : null)}
      </Remote>
    );
  },
);
